import React, { FC, useState } from 'react';
import { useDirectusAuth } from '../../providers/DirectusAuthProvider';
import { useAppDispatch } from '../../hooks/redux';
import { navigateTo } from '../../store/slices/navigationSlice';
import { useLanguage } from '../../contexts/LanguageContext';

interface LogoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Optional callback after successful logout
}

export const LogoutModal: FC<LogoutModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { logout } = useDirectusAuth();
  const dispatch = useAppDispatch();
  const { t } = useLanguage();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogoutConfirm = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      // Navigate to auth screen after successful logout
      dispatch(navigateTo('auth'));
      onClose();
      // Call optional success callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Logout failed:', error);
      // Still navigate even if logout fails
      dispatch(navigateTo('auth'));
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleLogoutCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="logout-title"
      aria-describedby="logout-description"
      data-modal="logout"
    >
      <div className="bg-card rounded-lg p-6 max-w-sm w-full border border-border shadow-xl">
        <h3 id="logout-title" className="text-lg font-semibold mb-4 text-card-foreground">{t('logout.title')}</h3>
        <p id="logout-description" className="text-muted-foreground mb-6">{t('logout.description')}</p>
        <div className="flex space-x-3">
          <button
            onClick={handleLogoutCancel}
            disabled={isLoggingOut}
            className="flex-1 px-4 py-2 text-card-foreground bg-secondary rounded-lg hover:bg-secondary/80 transition-colors font-medium disabled:opacity-50"
            aria-label="Cancel logout"
          >
            {t('logout.cancel')}
          </button>
          <button
            onClick={handleLogoutConfirm}
            disabled={isLoggingOut}
            className="flex-1 px-4 py-2 text-destructive-foreground bg-destructive rounded-lg hover:bg-destructive/90 transition-colors disabled:opacity-50 font-medium"
            aria-label="Confirm logout"
          >
            {isLoggingOut ? t('logout.logging_out') : t('logout.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Hook for using logout functionality
export const useLogout = () => {
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const triggerLogout = () => {
    setShowLogoutModal(true);
  };

  const closeLogoutModal = () => {
    setShowLogoutModal(false);
  };

  return {
    showLogoutModal,
    triggerLogout,
    closeLogoutModal,
  };
};